import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  LayoutDashboard,
  MessageSquareText,
  Reply,
  CalendarCheck,
  Calendar,
  Wrench,
  Settings,
  Link2,
  FileText,
  LogOut,
  User,
  Chrome,
  Sparkles,
  AlertTriangle,
  BarChart3,
  Activity,
  Users
} from 'lucide-react';

const navigation = [
  { name: 'Tableau de bord', href: '/', icon: LayoutDashboard },
  { name: 'Missions terrain', href: '/missions', icon: Wrench },
  { name: 'Calendrier missions', href: '/calendar', icon: Calendar },
  { name: 'Quality Insights', href: '/quality-insights', icon: Activity },
  { name: 'Alertes & Problèmes', href: '/issues', icon: AlertTriangle, badgeKey: 'issues' },
  { name: 'Carnet de contacts', href: '/contractors', icon: Users },
  { name: 'Rapports', href: '/reports', icon: BarChart3 },
  { name: 'À traiter', href: '/eligible', icon: Sparkles },
  { name: 'Avis voyageurs', href: '/guest-reviews', icon: MessageSquareText },
  { name: 'Réponses aux avis', href: '/host-replies', icon: Reply },
];

const configNav = [
  { name: 'Configuration', href: '/beds24', icon: Link2 },
  { name: 'Extension Chrome', href: '/extension', icon: Chrome },
  { name: 'Paramètres', href: '/settings', icon: Settings },
  { name: 'Logs & Audit', href: '/logs', icon: FileText },
];

export const Sidebar = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [issueBadge, setIssueBadge] = useState(0);

  useEffect(() => {
    if (!token) return;
    const fetchIssueCount = async () => {
      try {
        const apiUrl = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${apiUrl}/api/guest-comments/issues/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIssueBadge(res.data.new_issues || 0);
      } catch {
        // Silently fail
      }
    };
    fetchIssueCount();
    const interval = setInterval(fetchIssueCount, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside className="flex h-screen w-64 flex-col bg-white border-r border-slate-200 fixed left-0 top-0 z-40">
      {/* Logo */}
      <div className="flex h-16 items-center px-4 border-b border-slate-100">
        <img
          src="/logo-goodtime.png"
          alt="GoodTime"
          className="h-10 w-auto object-contain"
          data-testid="sidebar-logo"
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-1">
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 mb-2">
          Navigation
        </p>
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                isActive
                  ? 'bg-[#FF5A6E] text-white shadow-md'
                  : 'text-slate-600 hover:text-[#FF5A6E] hover:bg-[#FFF0F1]'
              }`
            }
          >
            <item.icon size={18} strokeWidth={1.5} />
            <span className="flex-1">{item.name}</span>
            {item.badgeKey === 'issues' && issueBadge > 0 && (
              <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="issue-badge">
                {issueBadge > 99 ? '99+' : issueBadge}
              </span>
            )}
          </NavLink>
        ))}

        <div className="pt-6">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-3 mb-2">
            Configuration
          </p>
          {configNav.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-[#FF5A6E] text-white shadow-md'
                    : 'text-slate-600 hover:text-[#FF5A6E] hover:bg-[#FFF0F1]'
                }`
              }
            >
              <item.icon size={18} strokeWidth={1.5} />
              {item.name}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* User section */}
      <div className="border-t border-slate-100 p-4">
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50">
          <div className="w-8 h-8 rounded-full bg-[#FF5A6E] flex items-center justify-center">
            <User size={14} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{user?.role}</p>
          </div>
          <button
            onClick={handleLogout}
            className="p-1.5 rounded-md hover:bg-slate-200 text-slate-500 transition-colors"
            title="Déconnexion"
            data-testid="logout-btn"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
